import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Avatar, 
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip
} from '@mui/material';
import { getChatMessages } from '../services/chats';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';

const ChatMessages = ({ chatId, onBack }) => {
  const navigate = useNavigate();
  const [chatData, setChatData] = useState({
    messages: [],
    users: {},
    hasMore: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [imagePreview, setImagePreview] = useState(null);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Load chat messages
  useEffect(() => {
    const fetchMessages = async () => {
      if (!chatId) return;
      
      setLoading(true);
      setError('');
      
      try {
        const data = await getChatMessages(chatId, 0, 50);
        setChatData(data);
      } catch (err) {
        console.error('Error fetching messages:', err);
        setError('Failed to load messages. ' + (err.message || ''));
      } finally {
        setLoading(false);
      }
    };
    
    fetchMessages();
    setPage(0);
  }, [chatId]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (!loading && chatData.messages.length > 0 && page === 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loading, chatData.messages, page]);

  // Load more messages
  const handleLoadMore = async () => {
    if (loading || !chatData.hasMore) return;
    
    const nextPage = page + 1;
    
    setLoading(true);
    try {
      const newData = await getChatMessages(chatId, nextPage, 50);
      
      // Combine old and new messages, avoiding duplicates
      const combinedMessages = [
        ...newData.messages, 
        ...chatData.messages
      ];
      
      setChatData({
        messages: combinedMessages,
        users: { ...chatData.users, ...newData.users },
        hasMore: newData.hasMore
      });
      setPage(nextPage);
    } catch (err) {
      setError('Failed to load more messages. ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    if (isToday) {
      return time;
    }
    
    const formattedDate = date.toLocaleDateString([], { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
    
    return `${formattedDate}, ${time}`;
  };

  // Get user info from ID
  const getUserInfo = (userId) => {
    return chatData.users[userId] || { name: 'Unknown User', avatar: null };
  };

  // Open full-screen image preview
  const handleImageClick = (imageUrl) => {
    setImagePreview(imageUrl);
  };

  // Close image preview
  const handleClosePreview = () => {
    setImagePreview(null);
  };
  
  // Navigate to user details page
  const handleUserClick = (userId) => {
    // Save the current state to be restored when returning
    // Get the query parameters from the URL to find the owner of the chat
    const urlParams = new URLSearchParams(window.location.pathname);
    const pathParts = window.location.pathname.split('/');
    const chatOwnerUserId = pathParts[3]; // Extract user ID from URL /dashboard/user/:userId/chats
    
    // Save both the chat ID and the owner user ID
    sessionStorage.setItem('returnToChatId', chatId);
    sessionStorage.setItem('returnToUserChats', chatOwnerUserId);
    
    // Navigate to user details
    navigate(`/dashboard/user/${userId}`);
  };

  if (!chatId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">Select a chat to view messages</Typography>
      </Box>
    );
  }

  if (loading && page === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && chatData.messages.length === 0) {
    return (
      <Box sx={{ p: 3, color: 'error.main' }}>
        <Typography variant="subtitle1">Error</Typography>
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        mt: 2, 
        display: 'flex', 
        flexDirection: 'column', 
        height: '600px'
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            edge="start" 
            color="inherit" 
            onClick={onBack} // Using the parent-provided onBack handler
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">Chat Messages</Typography>
        </Box>
        
        {/* Chat participants quick access */}
        {Object.keys(chatData.users).length > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {Object.entries(chatData.users).map(([userId, user]) => (
              <Tooltip key={userId} title={`View ${user.name}'s Profile`} arrow>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleUserClick(userId)}
                  startIcon={
                    <Avatar 
                      src={user.avatar} 
                      alt={user.name}
                      sx={{ width: 24, height: 24 }}
                    >
                      <PersonIcon fontSize="small" />
                    </Avatar>
                  }
                >
                  {user.name}
                </Button>
              </Tooltip>
            ))}
          </Box>
        )}
      </Box>
      
      {/* Messages Container */}
      <Box 
        ref={messagesContainerRef}
        sx={{ 
          flexGrow: 1, 
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column' 
        }}
      >
        {/* Load More Button */}
        {chatData.hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Load More'}
            </Button>
          </Box>
        )}
        
        {/* Messages */}
        {chatData.messages.length === 0 ? (
          <Box sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <Typography color="text.secondary">No messages in this chat</Typography>
          </Box>
        ) : (
          <>
            {chatData.messages.map((message, index) => {
              const user = getUserInfo(message.sender);
              const showDate = index === 0 || (
                chatData.messages[index - 1].created &&
                message.created &&
                new Date(chatData.messages[index - 1].created).toDateString() !==
                new Date(message.created).toDateString()
              );
              
              return (
                <React.Fragment key={message.id || index}>
                  {/* Date Separator */}
                  {showDate && message.created && (
                    <Box 
                      sx={{ 
                        textAlign: 'center', 
                        my: 2,
                        position: 'relative'
                      }}
                    >
                      <Divider sx={{ position: 'absolute', top: '50%', left: 0, right: 0 }} />
                      <Typography 
                        variant="caption" 
                        component="span"
                        sx={{ 
                          bgcolor: 'background.paper', 
                          px: 2, 
                          position: 'relative',
                          color: 'text.secondary'
                        }}
                      >
                        {new Date(message.created).toLocaleDateString([], {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Message Bubble */}
                  <Box 
                    sx={{ 
                      display: 'flex',
                      mb: 2,
                      alignItems: 'flex-start' 
                    }}
                  >
                    <Tooltip title={`View ${user.name}'s Profile`} arrow>
                      <Avatar 
                        src={user.avatar}
                        alt={user.name}
                        sx={{ 
                          mr: 1.5, 
                          mt: 0.5,
                          cursor: 'pointer',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: '0 0 10px rgba(0,0,0,0.2)'
                          }
                        }}
                        onClick={() => handleUserClick(message.sender)}
                      >
                        <PersonIcon />
                      </Avatar>
                    </Tooltip>
                    
                    <Box sx={{ maxWidth: '80%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
                        <Typography 
                          variant="subtitle2" 
                          component="span"
                          sx={{ 
                            mr: 1,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                          onClick={() => handleUserClick(message.sender)}
                        >
                          {user.name}
                          <InfoIcon sx={{ fontSize: 14, ml: 0.5, color: 'primary.main' }} />
                        </Typography>
                        <Typography 
                          variant="caption" 
                          component="span" 
                          color="text.secondary"
                        >
                          {formatTime(message.created)}
                        </Typography>
                      </Box>
                      
                      {/* Message Content */}
                      <Box 
                        sx={{ 
                          bgcolor: 'grey.100', 
                          p: 1.5, 
                          borderRadius: 2,
                          borderTopLeftRadius: 0
                        }}
                      >
                        {message.img && (
                          <Box 
                            sx={{ 
                              position: 'relative',
                              mb: message.text ? 1 : 0,
                              cursor: 'pointer',
                              '&:hover .zoom-icon': {
                                opacity: 1
                              }
                            }}
                            onClick={() => handleImageClick(message.img)}
                          >
                            <img 
                              src={message.img} 
                              alt="Message attachment" 
                              style={{ 
                                maxWidth: '100%', 
                                maxHeight: '300px',
                                borderRadius: '8px',
                                display: 'block'
                              }}
                            />
                            <Box 
                              className="zoom-icon"
                              sx={{ 
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                bgcolor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                borderRadius: '50%',
                                p: 1,
                                opacity: 0,
                                transition: 'opacity 0.2s'
                              }}
                            >
                              <ZoomOutMapIcon />
                            </Box>
                          </Box>
                        )}
                        
                        {message.text && (
                          <Typography 
                            variant="body1"
                            sx={{ 
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}
                          >
                            {message.text}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>
      
      {/* Image Preview Dialog */}
      <Dialog 
        open={!!imagePreview} 
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8, 
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.7)'
              }
            }}
            onClick={handleClosePreview}
          >
            <CloseIcon />
          </IconButton>
          {imagePreview && (
            <img 
              src={imagePreview} 
              alt="Full size attachment" 
              style={{ 
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

// Add a message date separator divider
const Divider = ({ ...props }) => (
  <Box 
    component="hr" 
    sx={{ 
      border: 'none', 
      borderTop: '1px solid',
      borderColor: 'divider',
      my: 2,
      ...props.sx 
    }} 
  />
);

export default ChatMessages;